import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { adnEnv } from '@/services/adUnits/adUnitTags'
import { CLICK_CHECK_PARAM } from '@/services/creatives/previewApi'
import {
  PREVIEW_EVENT_SOURCE,
  type PreviewEvent,
  type PreviewEventMessage,
} from '@/services/creatives/previewEvents'

const ADN_SCRIPT_URL = 'https://cdn.adnuntius.com/adn.js'


const TARGET_ID = 'adn-creative-preview'

interface AdnVisibility {
  viewability?: number
}

interface AdnPreviewCreative {
  creativeId: string
  lineItemId?: string
  targetId: string
  creativeWidth?: number
  creativeHeight?: number
  onImpressionResponse: () => void
  onPageLoad: () => void
  onVisible: (data?: AdnVisibility) => void
  onViewable: (data?: AdnVisibility) => void
}

interface Adn {
  calls: Array<() => void>
  request?: (request: {
    env?: string
    networkId: string
    creatives: AdnPreviewCreative[]
    onError: (data?: unknown) => void
  }) => void
}

declare global {
  interface Window {
    adn?: Adn
  }
}

/** The creative's postMessages to adn.js that are worth showing, by their message type. */
interface AdnChildMessage {
  messageSrc?: string
  messageType?: string
  customArgs?: { customType?: string; events?: Array<{ customType?: string }> }
}

function parseAdnMessage(data: unknown): AdnChildMessage | null {
  let message = data
  if (typeof message === 'string') {
    try {
      message = JSON.parse(message)
    } catch {
      return null
    }
  }
  if (typeof message !== 'object' || message === null) return null
  const parsed = message as AdnChildMessage
  return parsed.messageSrc === 'adn' ? parsed : null
}

function customEventNames(message: AdnChildMessage): string[] {
  const args = message.customArgs
  const events = args?.events ?? [{ customType: args?.customType }]
  return events.map((e) => e.customType).filter((name): name is string => !!name)
}

/** The creative's own frame, which adn.js inserts into the target. */
function creativeFrame(): HTMLIFrameElement | null {
  return document.getElementById(TARGET_ID)?.querySelector('iframe') ?? null
}

/**
 * Renders a saved creative through adn.js, as a publisher's page would, and
 * reports what it fires to the preview window around it. It is meant to be
 * framed by that window, which sizes it to the device being previewed.
 */
export function CreativePreviewRenderPage() {
  const { creativeId } = useParams<{ creativeId: string }>()
  const [params] = useSearchParams()
  const { t } = useTranslation()
  const [failed, setFailed] = useState(false)

  const networkId = params.get('network') ?? ''
  const checkingClick = params.has(CLICK_CHECK_PARAM)

  /**
   * The ad server reads the click-check flag off the Referer of the click, and
   * the modern default policy sends only the origin to another site — which
   * drops the flag. A looser policy is set for as long as the check is armed,
   * and only on this frame, so the rest of the app keeps the default.
   */
  useEffect(() => {
    if (!checkingClick) return
    const meta = document.createElement('meta')
    meta.name = 'referrer'
    meta.content = 'no-referrer-when-downgrade'
    document.head.appendChild(meta)
    return () => meta.remove()
  }, [checkingClick])

  const lineItemId = params.get('lineItem') ?? undefined
  const width = Number(params.get('width')) || undefined
  const height = Number(params.get('height')) || undefined

  useEffect(() => {
    if (!creativeId || !networkId) return
    const started = performance.now()
    // adn.js runs queued calls whenever it loads, so a call left behind by a
    // cleaned-up effect has to stand down itself
    let cancelled = false

    const report = (event: Omit<PreviewEvent, 'time'>) => {
      const message: PreviewEventMessage = {
        source: PREVIEW_EVENT_SOURCE,
        event: { ...event, time: Math.round(performance.now() - started) },
      }
      if (window.parent !== window) window.parent.postMessage(message, window.location.origin)
    }

    const frameSize = () => {
      const frame = creativeFrame()
      return frame ? `${frame.offsetWidth}×${frame.offsetHeight}` : undefined
    }

    const onMessage = (e: MessageEvent) => {
      const message = parseAdnMessage(e.data)
      if (!message) return
      switch (message.messageType) {
        case 'toParentImpression':
          report({ kind: 'impression' })
          break
        case 'toParentResize':
          // adn.js resizes the frame as it handles the message; measure after it has
          setTimeout(() => report({ kind: 'resize', detail: frameSize() }), 0)
          break
        case 'toParentCustomEvent':
          customEventNames(message).forEach((name) => report({ kind: 'custom', name }))
          break
      }
    }
    window.addEventListener('message', onMessage)

    // The creative's frame shares this origin (adn.js fills it through srcdoc),
    // so its clicks can be watched directly. A click on a link is a click-through.
    const watched = new WeakSet<Document>()
    const watchFrame = () => {
      let doc: Document | null = null
      try {
        doc = creativeFrame()?.contentDocument ?? null
      } catch {
        doc = null
      }
      if (!doc || watched.has(doc)) return
      watched.add(doc)
      doc.addEventListener(
        'click',
        (e) => {
          const link = (e.target as Element | null)?.closest?.('a[href]')
          if (link) report({ kind: 'click', detail: link.getAttribute('href') ?? undefined })
          else report({ kind: 'interaction' })
        },
        true
      )
    }

    // A frame on another origin hides its clicks, but focus moving into it still shows one
    const onBlur = () => {
      const frame = creativeFrame()
      if (!frame || document.activeElement !== frame) return
      try {
        if (frame.contentDocument) return
      } catch {
        // cross-origin: fall through and report the interaction
      }
      report({ kind: 'interaction' })
    }
    window.addEventListener('blur', onBlur)

    if (!document.querySelector(`script[src="${ADN_SCRIPT_URL}"]`)) {
      const script = document.createElement('script')
      script.src = ADN_SCRIPT_URL
      script.async = true
      document.head.appendChild(script)
    }

    const adn = (window.adn = window.adn || { calls: [] })
    adn.calls = adn.calls || []
    adn.calls.push(() => {
      if (cancelled) return
      window.adn?.request?.({
        env: adnEnv() ?? undefined,
        networkId,
        creatives: [
          {
            creativeId,
            lineItemId,
            targetId: TARGET_ID,
            creativeWidth: width,
            creativeHeight: height,
            onImpressionResponse: () => report({ kind: 'response' }),
            onPageLoad: () => {
              watchFrame()
              report({ kind: 'pageLoad', detail: frameSize() })
            },
            onVisible: () => report({ kind: 'visible' }),
            onViewable: () => report({ kind: 'viewable' }),
          },
        ],
        onError: () => {
          setFailed(true)
          report({ kind: 'error' })
        },
      })
    })

    return () => {
      cancelled = true
      window.removeEventListener('message', onMessage)
      window.removeEventListener('blur', onBlur)
    }
  }, [creativeId, networkId, lineItemId, width, height])

  const unavailable = !creativeId || !networkId || failed

  return (
    <div className="min-h-screen bg-card text-card-foreground">
      {unavailable ? (
        <p className="p-4 text-sm text-(--ad-text-muted)">
          {t('creatives.previewBlankPageFailed')}
        </p>
      ) : (
        <div id={TARGET_ID} />
      )}
    </div>
  )
}
